/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useState } from 'react';

import { BrandingUiInfoKeys } from '../../../../core/entities/BrandingUiInfoKeys';
import { ServicesClientImpl } from '../../../../core/interactors';
import { Locale, LocaleKeys } from '../../../../core/locale';
import { CursorManager, KeyManager } from '../../../../core/navigation';
import {
    RootState,
    useTivoDispatch,
    useTivoSelector,
} from '../../../../core/tivo/tivo-redux-wrapper';
import {
    ratingLimitsSetOptionLimitListAction,
    ratingLimitsSetSelectedOptionListAction,
    ratingLimitsSetVerticalLimitAction,
} from '../../../../store/settings/parentalcontrols/setratinglimits/setratinglimits.actions';
import { onCursorStateChangeSetRatingLimitsOperations } from '../../../../store/settings/parentalcontrols/setratinglimits/setratinglimits.reducers';
import { ClockTypes } from '../../../Clock';
import { AntiburnOsd, ScreenHeader, ScreenPreview } from '../../../common';
import { OptionList } from '../OptionList';

import './SetRatingLimits.scss';

const SetRatingLimits: FunctionComponent = (): JSX.Element => {
    const dispatch = useTivoDispatch();
    const services = new ServicesClientImpl();
    const { verticalPosition, selectedOptionList, isGhostModeEnabled } = useTivoSelector(
        (state: RootState) => state.setRatingLimits,
    );
    const { isActive: isAntiburnOsdActive } = useTivoSelector(
        (state: RootState) => state.antiburnOsd,
    );

    const [allowedRatings, setAllowedRatings] = useState<string[]>([]);
    const [blockedRatings, setBlockedRatings] = useState<string[]>([]);
    const [ratingDescription, setRatingDescription] = useState('');
    const [ratingNote, setRatingNote] = useState('');
    const [isUnratedOptionSelected, setIsUnratedOptionSelected] = useState(false);

    const lockIcon = services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.PC_LOCKED_ICON);

    const setRatingLimitsOption = [
        ...services.getPCRatingsOptions(),
        ...services.getPCUnratedOptions(),
    ];

    const renderRatings = (ratings: string[]) => {
        return ratings.length > 0
            ? ratings.map((ratingName: string, index: number) => (
                  <div className="ratings" key={`${index}${ratingName}`}>
                      <div className="rating-text">{ratingName}</div>
                  </div>
              ))
            : Locale.getString(LocaleKeys.SETTINGS_PC_SET_RATING_LIMITS_NONE_PREVIEW_TEXT);
    };

    const renderRatingBlock = () => {
        return (
            <>
                <div className="ratings-allowed">
                    {Locale.getString(
                        LocaleKeys.SETTINGS_PC_SET_RATING_LIMITS_ALLOWED_PREVIEW_TEXT,
                    )}
                </div>
                <div className="ratings-area">{renderRatings(allowedRatings)}</div>
                <div className="ratings-blocked">
                    {Locale.getString(
                        LocaleKeys.SETTINGS_PC_SET_RATING_LIMITS_BLOCKED_PREVIEW_TEXT,
                    )}
                </div>
                <div className="ratings-area">{renderRatings(blockedRatings)}</div>
            </>
        );
    };

    const handleRatedContent = () => {
        const rating = services.getPCRatingsOptions()[verticalPosition];
        const ratingName = rating.options[selectedOptionList[verticalPosition]];
        const ratingDes = rating.descriptions[selectedOptionList[verticalPosition]];
        if (!rating) return;

        setIsUnratedOptionSelected(false);

        setAllowedRatings(services.getPCAllowedRatings(verticalPosition, ratingName));
        setBlockedRatings(services.getPCBlockedRatings(verticalPosition, ratingName));

        setRatingDescription(ratingDes);
    };

    const handleUnratedContent = (position: number) => {
        const unratedOptions = services.getPCUnratedOptions();
        const updatedIndex = position - unratedOptions.length;

        const rating = unratedOptions[updatedIndex];
        if (!rating) return;

        setRatingDescription(rating.descriptions[selectedOptionList[position]]);
        if (rating.note.length > 0) {
            setRatingNote(rating.note);
        } else setRatingNote('');
        setIsUnratedOptionSelected(true);
    };

    const onCursorStateChangeCallback = () => {
        dispatch(onCursorStateChangeSetRatingLimitsOperations());
    };

    useEffect(() => {
        const refCursorManager = CursorManager.subscribe(onCursorStateChangeCallback);
        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }
        return () => {
            CursorManager.unsubscribe(refCursorManager);
        };
    }, []);

    useEffect(() => {
        const highestAllowedRatingsIndex = services.getPCHighestAllowedRatings().map((rating) => {
            return Number(Object.values(rating)[0]);
        });

        const unratedRatingIndex = services.getPCUnratedContentAllowed().map((rating) => {
            return Number(Object.values(rating)[0]);
        });

        dispatch(ratingLimitsSetVerticalLimitAction(setRatingLimitsOption.length));
        dispatch(
            ratingLimitsSetSelectedOptionListAction([
                ...highestAllowedRatingsIndex,
                ...unratedRatingIndex,
            ]),
        );

        const ratingOptionLimitList = setRatingLimitsOption.map((rating) => {
            return rating.options.length;
        });

        dispatch(ratingLimitsSetOptionLimitListAction(ratingOptionLimitList));
    }, []);

    useEffect(() => {
        if (verticalPosition < setRatingLimitsOption.length / 2) {
            handleRatedContent();
        } else {
            handleUnratedContent(verticalPosition);
        }
    }, [selectedOptionList[verticalPosition], verticalPosition]);

    const ratingLimitOptions = setRatingLimitsOption.map((ele, index) => {
        return (
            <OptionList
                options={ele.options}
                isFocused={index === verticalPosition}
                text={`${ele.text}:`}
                id={index}
                key={`${index}${ele.text}`}
                horizontalPosition={selectedOptionList[index]}
                verticalPosition={verticalPosition}
                isGhostModeEnabled={isGhostModeEnabled}
                iconUrl={
                    index >= setRatingLimitsOption.length / 2 &&
                    // 0 is for Block all
                    selectedOptionList[index] === 0
                        ? `${process.env.PUBLIC_URL}${lockIcon?.imageUrl}`
                        : ''
                }
            />
        );
    });

    return (
        <div className="set-rating-limits-container">
            <ScreenHeader
                title={Locale.getString(LocaleKeys.SETTINGS_PC_SET_RATING_LIMITS_HEADER_TITLE_TEXT)}
                clockType={ClockTypes.DEFAULT_CLOCK}
            />
            <div className="set-rating-limits">
                <div className="description">
                    {Locale.getString(LocaleKeys.SETTINGS_PC_SET_RATING_LIMITS_DESCRIPTION)}
                </div>
                {ratingLimitOptions}
            </div>
            <ScreenPreview classStyle="preview">
                {
                    <React.Fragment>
                        {!isUnratedOptionSelected ? (
                            <div className="rating-title">{`${Locale.getString(LocaleKeys.SETTINGS_PC_HIGHEST_ALLOWED_RATING_TEXT)}:`}</div>
                        ) : null}
                        <div className="rating-description">{ratingDescription}</div>
                        {isUnratedOptionSelected ? (
                            <div className="ratings-note">{ratingNote}</div>
                        ) : null}
                        {!isUnratedOptionSelected ? renderRatingBlock() : null}
                    </React.Fragment>
                }
            </ScreenPreview>
            {isAntiburnOsdActive && <AntiburnOsd />}
        </div>
    );
};

export default SetRatingLimits;
